/**
 * Busie ETP Widget SDK
 *
 * Minimal SDK for embedding the ETP Widget Web Component on customer sites.
 * Handles dynamic loading of the Web Component bundle and provides a simple API.
 *
 * Usage:
 * ```html
 * <script src="https://cdn.getbusie.com/sdk.js"></script>
 * <script>
 *   const busie = new Busie({ apiKey: 'your-api-key' });
 *   busie.mount('#widget-container');
 *   busie.on('quoteSubmitted', (data) => console.log('Quote:', data.quoteId));
 * </script>
 * <div id="widget-container"></div>
 * ```
 */

import { captureAndPersistAttribution } from './utils/attribution'
import { getVisitorId, isFingerprintEnabled } from './fingerprint'
import env from './utils/env'

// SDK configuration options
export interface BusieConfig {
  /** Organization API key (required) */
  apiKey: string
  /** Override visitor ID (optional - auto-generated via Fingerprint.js or localStorage if not provided) */
  visitorId?: string
  /** Override locale (optional - auto-detected if not provided) */
  locale?: string
  /** Use metric system for distances (optional - auto-detected based on locale) */
  useMetricSystem?: boolean
  /** CDN URL for Web Component bundle (optional - defaults to production CDN) */
  cdnUrl?: string
  /** Enable/disable fingerprinting (optional - defaults to true if API key is configured in env) */
  fingerprintEnabled?: boolean
  /** Timeout for fingerprinting in milliseconds (optional - defaults to 5000ms) */
  fingerprintTimeout?: number
}

// Event callback types
export type QuoteSubmittedCallback = (data: { quoteId: string }) => void
export type ErrorCallback = (data: { message: string }) => void
export type StepChangedCallback = (data: { step: number; name: string }) => void

// Event types that can be subscribed to
export type BusieEventType = 'quoteSubmitted' | 'error' | 'stepChanged'

// Callback map type
type EventCallbacks = {
  quoteSubmitted: QuoteSubmittedCallback[]
  error: ErrorCallback[]
  stepChanged: StepChangedCallback[]
}

/**
 * Generate a unique visitor ID for anonymous tracking
 * Uses crypto.randomUUID if available, falls back to timestamp-based ID
 */
function generateVisitorId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback for older browsers
  return `visitor-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Get or create a persistent visitor ID from localStorage
 */
function getOrCreateVisitorId(): string {
  const storageKey = 'busie_visitor_id'
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored) return stored
    const newId = generateVisitorId()
    localStorage.setItem(storageKey, newId)
    return newId
  } catch {
    // localStorage not available (e.g., private browsing)
    return generateVisitorId()
  }
}

/** Default fingerprint timeout in milliseconds */
const DEFAULT_FINGERPRINT_TIMEOUT = 5000

/**
 * Busie ETP Widget SDK
 *
 * Provides a simple interface for embedding the ETP Widget on customer sites.
 */
class Busie {
  private config: Required<Pick<BusieConfig, 'apiKey'>> & Omit<BusieConfig, 'apiKey'>
  private visitorId: string | null = null
  private fingerprintPromise: Promise<string> | null = null
  private widget: HTMLElement | null = null
  private container: HTMLElement | null = null
  private callbacks: EventCallbacks = {
    quoteSubmitted: [],
    error: [],
    stepChanged: [],
  }
  private loaded = false

  // Default CDN URL for production
  private static DEFAULT_CDN_URL = env.cdnUrl

  constructor(config: BusieConfig) {
    if (!config.apiKey) {
      throw new Error('[Busie SDK] apiKey is required')
    }

    // Capture marketing attribution on the operator page as early as possible —
    // this is the real marketing URL; the shadow-DOM component may not see it (etp-bff §9).
    captureAndPersistAttribution()

    this.config = {
      ...config,
      cdnUrl: config.cdnUrl || Busie.DEFAULT_CDN_URL,
    }

    // If customer provides a custom visitorId, use it directly
    if (config.visitorId) {
      this.visitorId = config.visitorId
    } else {
      // Start fingerprinting in background (don't block constructor)
      // Only if enabled and API key is configured via environment
      const shouldUseFingerprint = isFingerprintEnabled() && config.fingerprintEnabled !== false

      if (shouldUseFingerprint) {
        this.fingerprintPromise = this.initFingerprint()
      }
    }
  }

  /**
   * Initialize fingerprinting with timeout
   */
  private async initFingerprint(): Promise<string> {
    const timeout = this.config.fingerprintTimeout || DEFAULT_FINGERPRINT_TIMEOUT

    try {
      const visitorId = await Promise.race([
        getVisitorId(this.config.apiKey),
        new Promise<string>((_, reject) =>
          setTimeout(() => reject(new Error('Fingerprint timeout')), timeout)
        ),
      ])

      this.visitorId = visitorId
      return visitorId
    } catch (error) {
      console.warn('[Busie SDK] Fingerprint failed, using fallback:', error)
      this.visitorId = getOrCreateVisitorId()
      return this.visitorId
    }
  }

  /**
   * Get the current visitor ID
   */
  getVisitorId(): string | null {
    return this.visitorId
  }

  /**
   * Mount the widget to a container element
   *
   * @param selector - CSS selector or DOM element to mount the widget into
   * @returns Promise that resolves when widget is mounted
   */
  async mount(selector: string | HTMLElement): Promise<void> {
    // Find container element
    if (typeof selector === 'string') {
      const element = document.querySelector(selector)
      if (!element) {
        throw new Error(`[Busie SDK] Container element not found: ${selector}`)
      }
      this.container = element as HTMLElement
    } else {
      this.container = selector
    }

    // Wait for fingerprinting if in progress
    if (this.fingerprintPromise) {
      await this.fingerprintPromise
    }

    // Ensure we have a visitor ID (fallback if fingerprinting wasn't used or failed)
    if (!this.visitorId) {
      this.visitorId = getOrCreateVisitorId()
    }

    // Load Web Component bundle if not already loaded
    if (!this.loaded) {
      await this.loadBundle()
      this.loaded = true
    }

    // Create widget element
    this.widget = document.createElement('etp-widget')
    this.widget.setAttribute('organization-api-key', this.config.apiKey)
    this.widget.setAttribute('visitor-id', this.visitorId)
    this.widget.setAttribute('domain', window.location.hostname)

    // Set optional attributes
    if (this.config.locale) {
      this.widget.setAttribute('locale', this.config.locale)
    }
    if (this.config.useMetricSystem !== undefined) {
      this.widget.setAttribute('use-metric-system', String(this.config.useMetricSystem))
    }

    // Setup event listeners
    this.setupEventListeners()

    // Mount to container
    this.container.appendChild(this.widget)
  }

  /**
   * Subscribe to widget events
   *
   * @param event - Event type to subscribe to
   * @param callback - Callback function to execute when event fires
   */
  on(event: 'quoteSubmitted', callback: QuoteSubmittedCallback): void
  on(event: 'error', callback: ErrorCallback): void
  on(event: 'stepChanged', callback: StepChangedCallback): void
  on(
    event: BusieEventType,
    callback: QuoteSubmittedCallback | ErrorCallback | StepChangedCallback
  ): void {
    if (event === 'quoteSubmitted') {
      this.callbacks.quoteSubmitted.push(callback as QuoteSubmittedCallback)
    } else if (event === 'error') {
      this.callbacks.error.push(callback as ErrorCallback)
    } else if (event === 'stepChanged') {
      this.callbacks.stepChanged.push(callback as StepChangedCallback)
    }
  }

  /**
   * Unsubscribe from widget events
   *
   * @param event - Event type to unsubscribe from
   * @param callback - Callback function to remove
   */
  off(event: 'quoteSubmitted', callback: QuoteSubmittedCallback): void
  off(event: 'error', callback: ErrorCallback): void
  off(event: 'stepChanged', callback: StepChangedCallback): void
  off(
    event: BusieEventType,
    callback: QuoteSubmittedCallback | ErrorCallback | StepChangedCallback
  ): void {
    if (event === 'quoteSubmitted') {
      this.callbacks.quoteSubmitted = this.callbacks.quoteSubmitted.filter((cb) => cb !== callback)
    } else if (event === 'error') {
      this.callbacks.error = this.callbacks.error.filter((cb) => cb !== callback)
    } else if (event === 'stepChanged') {
      this.callbacks.stepChanged = this.callbacks.stepChanged.filter((cb) => cb !== callback)
    }
  }

  /**
   * Unmount the widget and cleanup
   */
  unmount(): void {
    if (this.widget && this.container) {
      this.widget.removeEventListener('quote-submitted', this.handleQuoteSubmitted)
      this.widget.removeEventListener('etp-error', this.handleError)
      this.widget.removeEventListener('step-changed', this.handleStepChanged)
      this.container.removeChild(this.widget)
      this.widget = null
    }
  }

  /**
   * Load the Web Component bundle dynamically
   */
  private async loadBundle(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already defined
      if (customElements.get('etp-widget')) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = `${this.config.cdnUrl}/etp-widget.wc.js`
      script.async = true

      script.onload = () => {
        // Wait for custom element to be defined
        customElements.whenDefined('etp-widget').then(() => resolve())
      }

      script.onerror = () => {
        reject(new Error('[Busie SDK] Failed to load Web Component bundle'))
      }

      document.head.appendChild(script)
    })
  }

  /**
   * Setup event listeners on the widget element
   */
  private setupEventListeners(): void {
    if (!this.widget) return

    this.widget.addEventListener('quote-submitted', this.handleQuoteSubmitted)
    this.widget.addEventListener('etp-error', this.handleError)
    this.widget.addEventListener('step-changed', this.handleStepChanged)
  }

  /**
   * Handle quote-submitted event from widget
   */
  private handleQuoteSubmitted = (event: Event): void => {
    const detail = (event as CustomEvent<{ quoteId: string }>).detail
    this.callbacks.quoteSubmitted.forEach((cb) => cb(detail))
  }

  /**
   * Handle etp-error event from widget
   */
  private handleError = (event: Event): void => {
    const detail = (event as CustomEvent<{ message: string }>).detail
    this.callbacks.error.forEach((cb) => cb(detail))
  }

  /**
   * Handle step-changed event from widget
   */
  private handleStepChanged = (event: Event): void => {
    const detail = (event as CustomEvent<{ step: number; name: string }>).detail
    this.callbacks.stepChanged.forEach((cb) => cb(detail))
  }
}

// Default export - Rollup's IIFE build with `exports: 'default'` assigns this to window.Busie
export default Busie
